import { isHidden } from "@/lib/hidden-features";
import { useNavigate, Outlet, useLocation } from "react-router-dom";
import {
  Flex,
  useMantineTheme,
  useMantineColorScheme,
  Box,
  Card,
  BackgroundImage,
  Stack,
  Group,
  Title,Text,
  Tabs,
  TextInput,
  ScrollArea
} from "@mantine/core";
import { useElementSize } from "@mantine/hooks";
import { MagnifyingGlassIcon } from "@phosphor-icons/react";
import { useState, useEffect } from "react";


export default function PathFinderLayout() {
  const [, setView] = useState<"shortcut" | "atlas">("shortcut");
  const [activeTab, setActiveTab] = useState<string | null>("shortcut");
  const location = useLocation();

  const tabRoutes: Record<string, string> = {
    shortcut: "/app/path-finder/",
    atlas: "/app/path-finder/atlas",
  };
  const tabBackgrounds: Record<string, string> = {
    shortcut: "/assets/EC_helth.jpg",
    atlas: "/assets/In_Progress.jpg",
  };
  const backgroundImageSrc = tabBackgrounds[activeTab ?? "shortcut"];

  const size = useElementSize();
  const { colorScheme } = useMantineColorScheme();

  const navigate = useNavigate();
  const theme = useMantineTheme();

  // Update active tab based on current route
  useEffect(() => {
    const currentPath = location.pathname;
    if (currentPath === "/app/path-finder/atlas") {
      setActiveTab("atlas");
      setView("atlas");
    } else if (currentPath === "/app/path-finder" || currentPath === "/app/path-finder/") {
      setActiveTab("shortcut");
      setView("shortcut");
    }
  }, [location.pathname]);

  return (
    <Box p={"md"}>


       <Card p={0} radius="md" withBorder >
        <BackgroundImage
          src={backgroundImageSrc}
          radius="md"
          style={{
            backgroundPosition: "center 42%",
            backgroundSize: "cover",
            height: 180,

            width: "100%",
          }}
        >
          <Box
            p="xl"
            style={{
              position: "absolute",
              top: 80,
              left: 0,
              right: 0,
              height: "100%",

              borderRadius: "inherit",
              zIndex: 1,
            }}
          >
            <Group justify="space-between" align="flex-start" h="100%">
              <Stack gap={4}>
                <Title order={3} c="#FAFAFA" fw={700} size="xl">
                  PathFinder AI Assistant
                </Title>
                <Text c="#FAFAFA" fw={700} size="md">
                  Ask questions and get personalized health guidance powered by
                  AI.
                </Text>
              </Stack>
            </Group>
          </Box>
        </BackgroundImage>
      </Card>

      <Group justify="space-between" align="flex-start" mt="sm" wrap="wrap">
        <Tabs
          value={activeTab}
          onChange={(tab) => {
            setActiveTab(tab);
            if (tab && tabRoutes[tab]) {
              navigate(tabRoutes[tab]);
            }
          }}
          radius="md"
          variant="pills"
          defaultValue="new"
        >
          <Tabs.List
              
            bg={
              colorScheme === "dark"
                ? theme.colors.neutral[9]
                : theme.colors.neutral[2]
            }
            style={{ borderRadius: "10px" }}
            p={"xs"}
          >
            <Tabs.Tab 
              value="shortcut"
              style={{
                backgroundColor: activeTab === "shortcut" ? theme.colors.primary[7] : "transparent",
                color: activeTab === "shortcut" ? "white" : undefined,
              }}
              onMouseEnter={(e) => {
                if (activeTab !== "shortcut") {
                  e.currentTarget.style.backgroundColor = colorScheme === "dark" 
                    ? "#555555"
                    : "white";
                }
              }}
              onMouseLeave={(e) => {
                if (activeTab !== "shortcut") {
                  e.currentTarget.style.backgroundColor = "transparent";
                } else {
                  e.currentTarget.style.backgroundColor = theme.colors.primary[7];
                }
              }}
            >
              Shortcut
            </Tabs.Tab>
            {!isHidden("atlas") && (
            <Tabs.Tab 
              value="atlas"
              style={{
                backgroundColor: activeTab === "atlas" ? theme.colors.primary[7] : "transparent",
              }}
              onMouseEnter={(e) => {
                if (activeTab !== "atlas") {
                  e.currentTarget.style.backgroundColor = colorScheme === "dark" 
                    ? "#555555"
                    : "white";
                }
              }}
              onMouseLeave={(e) => {
                if (activeTab !== "atlas") {
                  e.currentTarget.style.backgroundColor = "transparent";
                } else {
                  e.currentTarget.style.backgroundColor = theme.colors.primary[7];
                }
              }}
            >
              Atlas
            </Tabs.Tab>
            )}
          </Tabs.List>
        </Tabs>

        {activeTab === "atlas" && (
          <TextInput
            placeholder="Search by health topic here..."
            leftSection={<MagnifyingGlassIcon size={16} />}
            w={{ base: "100%", sm: "300px" }}
            size="md"
            style={{ minWidth: "250px" }}
          />
        )}
      </Group>

      <Flex style={{ flexGrow: 1 }} direction={"column"} ref={size.ref} mt="md">
        <ScrollArea.Autosize h={"calc(100vh - 280px)"}>
          <Outlet />
        </ScrollArea.Autosize>
      </Flex>
    </Box>
  );
}
